/** Staff: fix a live/ordered website's contact details (not its content) and rebuild + republish in one click. */
import React, { useState } from 'react';
import { Loader2, Save, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { wpatch } from './api';

const FIELDS = [
  ['telephone', 'Telephone'], ['whatsapp', 'WhatsApp'], ['email', 'Public email'], ['enquiry_email', 'Enquiries go to (email)'], ['enquiry_mobile', 'Enquiries go to (mobile)'],
  ['facebook', 'Facebook URL'], ['instagram', 'Instagram URL'], ['tiktok', 'TikTok URL'], ['linkedin', 'LinkedIn URL'], ['youtube', 'YouTube URL'],
];

export function ContactEditor({ order, onDone }) {
  const f = order.form || {};
  const init = { ...FIELDS.reduce((a, [k]) => ({ ...a, [k]: '' }), {}), telephone: f.business?.telephone || '', whatsapp: f.business?.whatsapp || '', email: f.business?.email || '', address: f.business?.address || '', opening_hours: f.business?.opening_hours || '',
    enquiry_email: f.enquiries?.email || '', enquiry_mobile: f.enquiries?.mobile || '', ...Object.fromEntries(['facebook', 'instagram', 'tiktok', 'linkedin', 'youtube'].map((k) => [k, f.social?.[k] || ''])) };
  const [v, setV] = useState(init);
  const [busy, setBusy] = useState(false);
  const dirty = Object.keys(v).some((k) => v[k] !== init[k]);
  const save = async (republish) => {
    setBusy(true);
    try {
      const changed = Object.fromEntries(Object.entries(v).filter(([k, val]) => val !== init[k]));
      const d = await wpatch(`/staff/website/orders/${order.id}/contact`, { ...changed, republish });
      toast.success(d.republish?.queued ? 'Saved — rebuilding and republishing in the background' : 'Saved');
      onDone?.(d.rollout);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="wod-contact-editor">
      <p className="text-xs text-gray-500">Change the details shown on the website — phone, email, address, hours, socials. The design and wording stay as they are.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {FIELDS.map(([k, l]) => <div key={k}><Label className="text-xs">{l}</Label><Input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="h-9" data-testid={`wod-contact-${k}`} /></div>)}
        <div><Label className="text-xs">Address</Label><Textarea value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} rows={2} data-testid="wod-contact-address" /></div>
        <div><Label className="text-xs">Opening hours</Label><Textarea value={v.opening_hours} onChange={(e) => setV({ ...v, opening_hours: e.target.value })} rows={2} data-testid="wod-contact-opening_hours" /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={busy || !dirty} onClick={() => save(false)} data-testid="wod-contact-save"><Save className="w-3.5 h-3.5 mr-1" /> Save only</Button>
        <Button size="sm" disabled={busy || !dirty} onClick={() => save(true)} className="bg-gray-900 text-white" data-testid="wod-contact-republish">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <UploadCloud className="w-3.5 h-3.5 mr-1" />} Save, rebuild &amp; republish</Button>
      </div>
    </div>
  );
}
